/**
 * Enough DOM helper to build the HUD, and no more.
 *
 * There is no framework in this project and adding one for six panels would be
 * a poor trade — the editor's budget belongs to three.js and the pixel work,
 * not to a runtime that re-renders text.
 *
 * The rule that matters: **`text` sets textContent, never innerHTML.** Project
 * titles, provider names and error messages all reach these functions, and
 * titles in particular are user-controlled. The server already strips control
 * characters and bidi overrides, but that is normalisation, not escaping —
 * escaping is the render layer's job, which is here.
 */

type Child = Node | string | null | undefined | false;

export interface ElementSpec {
  class?: string;
  text?: string;
  title?: string;
  href?: string;
  type?: string;
  disabled?: boolean;
  hidden?: boolean;
  /** `data-*` attributes, without the prefix. */
  data?: Record<string, string | number | boolean | null | undefined>;
  /** `aria-*` attributes, without the prefix. */
  aria?: Record<string, string | number | boolean | null | undefined>;
  on?: Partial<Record<keyof HTMLElementEventMap, (e: Event) => void>>;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  spec: ElementSpec = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (spec.class) node.className = spec.class;
  if (spec.text !== undefined) node.textContent = spec.text;
  if (spec.title !== undefined) node.title = spec.title;
  if (spec.hidden) node.hidden = true;
  if (spec.href !== undefined && node instanceof HTMLAnchorElement) node.href = spec.href;
  if (spec.type !== undefined && "type" in node) {
    (node as HTMLButtonElement).type = spec.type as HTMLButtonElement["type"];
  }
  if (spec.disabled && "disabled" in node) (node as HTMLButtonElement).disabled = true;

  for (const [key, value] of Object.entries(spec.data ?? {})) {
    if (value !== null && value !== undefined) node.dataset[key] = String(value);
  }
  for (const [key, value] of Object.entries(spec.aria ?? {})) {
    if (value !== null && value !== undefined) node.setAttribute(`aria-${key}`, String(value));
  }
  for (const [event, handler] of Object.entries(spec.on ?? {})) {
    node.addEventListener(event, handler as EventListener);
  }

  append(node, children);
  return node;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
}

/** Replace all children. The HUD re-renders whole panels rather than diffing. */
export function replace(parent: HTMLElement, ...children: Child[]): void {
  parent.replaceChildren();
  append(parent, children);
}

/**
 * Run something, ignoring calls that arrive while it is still running.
 *
 * The reentrancy guard, separated from the DOM so it can be tested. There is no
 * DOM environment in this project's node suite, and this was the untested part
 * of a control that fronts **checkout and account deletion** — a double-click
 * opening two Stripe Checkout sessions, or firing two deletion requests, is not
 * a cosmetic bug.
 *
 * Deliberately *drops* the extra call rather than queueing it. Queueing would
 * mean the second click still eventually opens a second Checkout session, just
 * later, which is the same problem with worse timing.
 */
export function once<T>(run: () => Promise<T> | T): {
  invoke: () => Promise<T | undefined>;
  busy: () => boolean;
} {
  let inFlight = false;
  return {
    busy: () => inFlight,
    invoke: async () => {
      if (inFlight) return undefined;
      inFlight = true;
      try {
        return await run();
      } finally {
        // Released even when `run` throws, or a failed attempt would wedge the
        // control permanently and the only way out would be a reload.
        inFlight = false;
      }
    },
  };
}

/** A button that cannot double-fire, and says so while it is working. */
export function actionButton(
  label: string,
  run: () => Promise<void> | void,
  spec: ElementSpec = {},
): HTMLButtonElement {
  const button = el("button", { ...spec, type: "button", text: label });
  const guard = once(run);

  button.addEventListener("click", async () => {
    if (guard.busy()) return;
    button.disabled = true;
    button.dataset.busy = "true";
    try {
      await guard.invoke();
    } finally {
      button.disabled = Boolean(spec.disabled);
      delete button.dataset.busy;
    }
  });

  return button;
}

/** Absolute time, spelled out. Relative time lies about anything over a week. */
export function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Relative, for recent things only — falls back to a date past a week. */
export function formatRelative(ms: number, now = Date.now()): string {
  const seconds = Math.round((now - ms) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(ms);
}
